import {
  ORGANIZATION_SKILL_INSTALL_PATH_SEGMENT,
  ORGANIZATION_SKILL_PUBLISH_PATH,
  ORGANIZATION_SKILL_UPDATE_PATH_SEGMENT,
  ORGANIZATION_SKILL_VERSIONS_PATH_SEGMENT,
  ORGANIZATION_SKILLS_BASE_PATH,
} from "../constants/skills";
import type {
  TInstallOrganizationCatalogSkillRequest,
  TListOrganizationCatalogQuery,
  TOrganizationCatalogEntryDetail,
  TOrganizationCatalogEntryMutationResponse,
  TOrganizationCatalogListResponse,
  TPublishOrganizationSkillGithubRequest,
  TPublishOrganizationSkillPasteRequest,
  TPublishOrganizationSkillRequest,
  TPublishOrganizationSkillZipRequest,
  TSkillWithResourcesMutationResponse,
  TUpdateInstalledCatalogSkillRequest,
  TUpdateOrganizationCatalogVersionStatusRequest,
} from "../types/skills";
import { encodePathSegment } from "../utils/encode-path-segment";
import type { ApiClient } from "./client";
import {
  appendSkillPagingParams,
  appendSkillSearchParam,
} from "./skill-query-params";

export interface IUpdateCatalogVersionStatusParams {
  entryUuid: string;
  versionUuid: string;
  payload: TUpdateOrganizationCatalogVersionStatusRequest;
}

export interface IInstallCatalogSkillParams {
  entryUuid: string;
  payload: TInstallOrganizationCatalogSkillRequest;
}

export interface IUpdateInstalledCatalogSkillParams {
  entryUuid: string;
  payload: TUpdateInstalledCatalogSkillRequest;
}

// NOTE: NSAI-3311 — the Organization Catalog client shared by web and mobile.
//
// Publishing goes straight through: there is no submission, approval, rejection
// or review-queue call here because there is no review queue. A curator publishes
// one exact immutable version and it becomes installable immediately.
//
// The catalog reads answer install-decision metadata only — never a skill body and
// never resource bytes. Install and update move bytes server-side inside S3, so
// there is no upload or download helper on this class either.
//
// The preview step is deliberately absent: a direct catalog import reuses the
// existing `/skills/import/*/preview` routes through SkillsApi, so the validation,
// digest, licence, compatibility, resource and scripts-present report a curator
// sees is the very same report the ordinary import flow produces.
export class OrganizationSkillsApi {
  constructor(private readonly apiClient: ApiClient) {}

  private buildEntryPath(entryUuid: string): string {
    return `${ORGANIZATION_SKILLS_BASE_PATH}/${encodePathSegment(entryUuid)}`;
  }

  async listCatalog({
    page,
    limit,
    search,
    status,
  }: TListOrganizationCatalogQuery = {}): Promise<TOrganizationCatalogListResponse> {
    const params = appendSkillPagingParams({
      params: new URLSearchParams(),
      page,
      limit,
    });
    appendSkillSearchParam({ params, search });

    if (status) {
      params.set("status", status);
    }

    const queryString = params.toString();
    const response = await this.apiClient.get<TOrganizationCatalogListResponse>(
      queryString
        ? `${ORGANIZATION_SKILLS_BASE_PATH}?${queryString}`
        : ORGANIZATION_SKILLS_BASE_PATH,
    );
    return response.data;
  }

  async getCatalogEntry(
    entryUuid: string,
  ): Promise<TOrganizationCatalogEntryDetail> {
    const response = await this.apiClient.get<TOrganizationCatalogEntryDetail>(
      this.buildEntryPath(entryUuid),
    );
    return response.data;
  }

  async publishExistingVersion(
    payload: TPublishOrganizationSkillRequest,
  ): Promise<TOrganizationCatalogEntryMutationResponse> {
    const response =
      await this.apiClient.post<TOrganizationCatalogEntryMutationResponse>(
        ORGANIZATION_SKILL_PUBLISH_PATH,
        payload,
      );
    return response.data;
  }

  async publishPaste(
    payload: TPublishOrganizationSkillPasteRequest,
  ): Promise<TOrganizationCatalogEntryMutationResponse> {
    const response =
      await this.apiClient.post<TOrganizationCatalogEntryMutationResponse>(
        `${ORGANIZATION_SKILL_PUBLISH_PATH}/paste`,
        payload,
      );
    return response.data;
  }

  async publishZip(
    payload: TPublishOrganizationSkillZipRequest,
  ): Promise<TOrganizationCatalogEntryMutationResponse> {
    const response =
      await this.apiClient.post<TOrganizationCatalogEntryMutationResponse>(
        `${ORGANIZATION_SKILL_PUBLISH_PATH}/zip`,
        payload,
      );
    return response.data;
  }

  async publishGithub(
    payload: TPublishOrganizationSkillGithubRequest,
  ): Promise<TOrganizationCatalogEntryMutationResponse> {
    const response =
      await this.apiClient.post<TOrganizationCatalogEntryMutationResponse>(
        `${ORGANIZATION_SKILL_PUBLISH_PATH}/github`,
        payload,
      );
    return response.data;
  }

  async updateVersionStatus({
    entryUuid,
    versionUuid,
    payload,
  }: IUpdateCatalogVersionStatusParams): Promise<TOrganizationCatalogEntryMutationResponse> {
    const response =
      await this.apiClient.patch<TOrganizationCatalogEntryMutationResponse>(
        `${this.buildEntryPath(entryUuid)}/${ORGANIZATION_SKILL_VERSIONS_PATH_SEGMENT}/${encodePathSegment(versionUuid)}`,
        payload,
      );
    return response.data;
  }

  async installCatalogSkill({
    entryUuid,
    payload,
  }: IInstallCatalogSkillParams): Promise<TSkillWithResourcesMutationResponse> {
    const response =
      await this.apiClient.post<TSkillWithResourcesMutationResponse>(
        `${this.buildEntryPath(entryUuid)}/${ORGANIZATION_SKILL_INSTALL_PATH_SEGMENT}`,
        payload,
      );
    return response.data;
  }

  async updateInstalledCatalogSkill({
    entryUuid,
    payload,
  }: IUpdateInstalledCatalogSkillParams): Promise<TSkillWithResourcesMutationResponse> {
    const response =
      await this.apiClient.post<TSkillWithResourcesMutationResponse>(
        `${this.buildEntryPath(entryUuid)}/${ORGANIZATION_SKILL_UPDATE_PATH_SEGMENT}`,
        payload,
      );
    return response.data;
  }
}
